import React, { useEffect, useState } from 'react';
import { X, Copy, Check, Lock, Loader2, AlertTriangle } from 'lucide-react';
import { PAY_CURRENCIES } from '../presaleConfig';
import { depositApi } from '../api';
import { useAuth } from '../AuthContext';
import { useToast } from '../hooks/use-toast';

const DepositModal = ({ open, onClose, amountUsd, bonusPercent, estimatedTokens }) => {
  const { toast } = useToast();
  const { refresh } = useAuth();
  const [step, setStep] = useState('choose');
  const [currency, setCurrency] = useState(PAY_CURRENCIES[0]);
  const [session, setSession] = useState(null);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(20 * 60);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [credited, setCredited] = useState(false);

  useEffect(() => {
    if (!open) {
      setStep('choose');
      setSession(null);
      setSecondsLeft(20 * 60);
      setCopied(false);
      setPaymentStatus(null);
      setCredited(false);
    }
  }, [open]);

  useEffect(() => {
    if (step !== 'session') return;
    const i = setInterval(() => setSecondsLeft(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(i);
  }, [step]);

  // Auto-poll NOWPayments status every 8s to trigger backend fallback credit
  useEffect(() => {
    if (step !== 'session' || !session?.payment_id || credited) return;
    let alive = true;
    const tick = async () => {
      try {
        const res = await depositApi.status(session.payment_id);
        if (!alive) return;
        const npStatus = res?.np_status?.payment_status;
        const sessCredited = res?.session?.credited;
        setPaymentStatus(npStatus);
        if (sessCredited) {
          setCredited(true);
          await refresh();
          toast({ title: '🎉 Paiement crédité !', description: 'Vos jetons $DOMIA ont été ajoutés à votre solde.' });
        }
      } catch (e) { /* silent */ }
    };
    tick();
    const id = setInterval(tick, 8000);
    return () => { alive = false; clearInterval(id); };
  }, [step, session?.payment_id, credited, refresh, toast]);

  if (!open) return null;

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await depositApi.create({ amount_usd: amountUsd, pay_currency: currency.code, purpose: 'presale' });
      setSession(res);
      setStep('session');
    } catch (e) {
      // If backend not ready (no DOMIA mint or NOWPayments error), use a mock display
      const detail = e?.response?.data?.detail || e.message;
      toast({ title: 'Using preview mode', description: 'Live payment will be enabled when the token is live.', });
      setSession({
        pay_address: 'DEMO_ADDRESS_' + Math.random().toString(36).slice(2, 14).toUpperCase(),
        pay_amount: (amountUsd / 0.998).toFixed(2),
        pay_currency: currency.code,
        amount_usd: amountUsd,
        order_id: 'PREVIEW-' + Date.now(),
        _preview: true,
        _error: detail,
      });
      setStep('session');
    } finally {
      setCreating(false);
    }
  };

  const copyAddress = () => {
    if (!session?.pay_address) return;
    navigator.clipboard.writeText(session.pay_address);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;

  // QR via free service (works offline as well by embedding text)
  const qrUrl = session?.pay_address
    ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=8&data=${encodeURIComponent(session.pay_address)}`
    : null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70]" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[71] w-[92%] max-w-[480px] max-h-[92vh] overflow-y-auto bg-white rounded-3xl shadow-2xl border border-slate-100">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-black text-slate-900">Deposit DOMIA</h2>
            <p className="text-[12px] text-slate-500 mt-0.5">{step === 'choose' ? 'Choose your payment network' : 'Send the exact amount below'}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center">
            <X className="w-4 h-4 text-slate-700" />
          </button>
        </div>

        {/* Summary */}
        <div className="px-5 pt-4">
          <div className="rounded-2xl p-4 text-white" style={{ background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 60%, #f59e0b 130%)' }}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] font-bold opacity-90 tracking-wide">AMOUNT</div>
                <div className="text-2xl font-black">${Number(amountUsd || 0).toLocaleString()}</div>
              </div>
              <div className="text-right">
                <div className="text-[11px] font-bold opacity-90 tracking-wide">YOU GET</div>
                <div className="text-2xl font-black">{Number(estimatedTokens || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} DOMIA</div>
                {bonusPercent > 0 && <div className="text-[11px] font-extrabold text-amber-200 mt-0.5">+{bonusPercent}% Bonus</div>}
              </div>
            </div>
          </div>
        </div>

        {step === 'choose' && (
          <div className="p-5">
            <div className="text-[11px] font-bold tracking-[0.14em] text-slate-500 mb-2">PAYMENT NETWORK</div>
            <div className="grid grid-cols-2 gap-2">
              {PAY_CURRENCIES.map(c => (
                <button key={c.code} onClick={() => setCurrency(c)} className={`flex items-center gap-3 rounded-xl border px-3 py-3 transition ${currency.code === c.code ? 'border-blue-500 bg-blue-50 shadow-[0_4px_14px_rgba(37,99,235,0.18)]' : 'border-slate-200 bg-white hover:border-blue-300'}`}>
                  {c.icon ? (
                    <img src={c.icon} alt={`${c.label} ${c.chain}`} className="w-10 h-10 rounded-full object-contain" />
                  ) : (
                    <span className="w-10 h-10 rounded-full flex items-center justify-center text-white text-[12px] font-black" style={{ background: c.color }}>
                      {c.label === 'USDC' ? '$' : 'T'}
                    </span>
                  )}
                  <div className="text-left">
                    <div className="text-[13px] font-black text-slate-900">{c.label}</div>
                    <div className="text-[11px] text-slate-500">{c.chain}</div>
                  </div>
                </button>
              ))}
            </div>

            <button onClick={handleCreate} disabled={creating} className="mt-5 w-full rounded-full py-3.5 font-extrabold text-white text-[14px] flex items-center justify-center gap-2 shadow-[0_10px_24px_rgba(37,99,235,0.45)] disabled:opacity-60" style={{ background: 'linear-gradient(90deg, #1d4ed8, #3b82f6)' }}>
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Continue to Payment
            </button>
            <p className="text-[11px] text-slate-500 text-center mt-3">Payments are processed securely via NOWPayments. Your DOMIA tokens will be credited automatically.</p>
          </div>
        )}

        {step === 'session' && session && (
          <div className="p-5">
            {session._preview && (
              <div className="mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2 text-[11px] text-amber-800">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span><b>Preview mode:</b> live payments will be enabled once the DOMIA token is fully deployed on mainnet. Design walkthrough only.</span>
              </div>
            )}

            <div className="text-center">
              <div className="inline-block rounded-2xl bg-white p-2 border border-slate-200 shadow-sm">
                {qrUrl && <img src={qrUrl} alt="QR" className="w-[220px] h-[220px]" />}
              </div>
              <div className="mt-2 text-[11px] text-slate-500">Scan with your wallet app</div>
              <div className="mt-3 inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-3 py-1 text-amber-700">
                <span className="text-[11px] font-bold tracking-wide">SESSION EXPIRES IN</span>
                <span className="font-black text-[13px]">{String(mins).padStart(2,'0')}:{String(secs).padStart(2,'0')}</span>
              </div>
              {/* Auto-polling status indicator */}
              {credited ? (
                <div className="mt-3 inline-flex items-center gap-2 bg-emerald-50 border border-emerald-300 rounded-full px-3 py-1 text-emerald-700" data-testid="deposit-credited">
                  <Check className="w-3.5 h-3.5" />
                  <span className="text-[11px] font-extrabold">🎉 Crédité ! Vos $DOMIA sont dans votre solde.</span>
                </div>
              ) : paymentStatus && paymentStatus !== 'waiting' ? (
                <div className="mt-3 inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full px-3 py-1 text-blue-700">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span className="text-[11px] font-bold uppercase">Statut: {paymentStatus} · crédit auto en cours…</span>
                </div>
              ) : (
                <div className="mt-3 inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full px-3 py-1 text-slate-600">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span className="text-[11px] font-bold uppercase">En attente du paiement…</span>
                </div>
              )}
            </div>

            <div className="mt-4">
              <div className="text-[11px] font-bold tracking-[0.14em] text-slate-500">SEND EXACTLY</div>
              <div className="mt-1 flex items-center justify-between rounded-xl bg-slate-50 border border-slate-200 px-3 py-3">
                <span className="text-xl font-black text-slate-900">{session.pay_amount}</span>
                <span className="text-[12px] font-extrabold text-blue-600 uppercase">{session.pay_currency}</span>
              </div>
            </div>

            <div className="mt-3">
              <div className="text-[11px] font-bold tracking-[0.14em] text-slate-500">TO ADDRESS</div>
              <div className="mt-1 flex items-center justify-between rounded-xl bg-slate-50 border border-slate-200 px-3 py-2.5 gap-2">
                <span className="text-[11.5px] font-mono text-slate-800 break-all flex-1">{session.pay_address}</span>
                <button onClick={copyAddress} className="shrink-0 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 text-[11px] font-extrabold flex items-center gap-1">
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>

            <button onClick={onClose} className="mt-4 w-full rounded-full py-3 font-extrabold text-slate-700 text-[13px] bg-white border border-slate-200 hover:bg-slate-50">
              I'll pay later
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default DepositModal;
